import type { FieldSchema, SegmentRule } from 'Listrak.Customer.Segmentation.Api.Client';
import { getRelativeDateText, operatorDictionary } from 'definitions/operator-dictionary/operator-dictionary';
import { aliases } from './aliases';

export const getOperatorDisplay = (rule: SegmentRule, ruleSchema: FieldSchema): string => {
  if (rule.operator === 'DaysFromToday') {
    return `Is ${getRelativeDateText(rule.values)}`;
  } else if (rule.operator === 'DaysFromTodayMonthDay') {
    return `Is ${getRelativeDateText(rule.values)}`;
  } else {
    const dataType = ruleSchema.dataType;
    return operatorDictionary[rule.operator].text[dataType];
  }
}

export const getValueDisplay = (rule: SegmentRule, ruleSchema: FieldSchema): string => {
  const suffix = rule.operator.includes('MonthDay') ? ' (MM/DD)' : '';
  const formatter = operatorDictionary[rule.operator]?.formatter?.[ruleSchema?.dataType];

  return formatter ? formatter(rule.values, ruleSchema) + suffix : '';
}

export const getBoolModifiersDisplay = (rule: SegmentRule, modifiersMap: Map<string, FieldSchema>): (string | HTMLElement)[] => {
  const enum iconEnum {
    'Timeframe' = 'clock',
    'SimpleNumeric' = 'hashtag',
  }
  return structuredClone(rule.modifiers).map((m: SegmentRule) => {
    const schema = modifiersMap.get(m.fieldUid);
    const operator = getOperatorDisplay(m, schema);
    const value = getValueDisplay(m, schema);
    const operatorGroup = schema.operatorGroup;
    const name = schema?.name ?? '';

    if (['StandardTextIs', 'StandardTextContains', 'NumericTextIs', 'MultiValueSelectionIs', 'InCommaList'].includes(operatorGroup)) {
      if (name === 'MessageUid') {
        return m.values.length > 1 ? 'Selected Messages' : 'Selected Message';
      }

      if ((name.includes('Message Channel') || name === 'Subscription Channel') && m.values.length) {
        const listValues = m.values.map((val, index) => {
          val = val.replace(/Click|Tap|Open|Send|Address|DeviceUid/, '');
          val = val.replace(/AppPush/, 'App Push');
          val = val.replace(/Sms/, 'SMS');
          val = val.replace(/PhoneNumber/, 'SMS');

          if (index !== m.values.length - 1 && m.values.length > 1) {
            if (m.values.length === 2) {
              val = val + ' ';
            } else {
              val = val + ', ';
            }
          }

          if (index === m.values.length - 1 && m.values.length > 1) {
            val = 'or ' + val;
          }

          return val;
        });

        return listValues.join('');
      }

      if (name === 'Exclude MPP Opens') {
        return 'Exclude MPP Opens';
      }

      return value;
    }

    if (operatorGroup === 'Timeframe' || operatorGroup === 'Date') {
      return `<atlas-icon size="0.7" name="${iconEnum.Timeframe}"></atlas-icon> ${operator.replace('Is ', '')} ${value}`;
    }

    if (operatorGroup === 'SimpleNumeric') {
      let prefix = '';

      if (['Over', 'Under'].includes(operator)) {
        prefix = operator.toLowerCase() + ' ';
      }

      return `<atlas-icon size="0.7" name="${iconEnum.SimpleNumeric}"></atlas-icon> ${prefix + value} times`;
    }
  });
}

export const getModifiersDisplay = (rule: SegmentRule, modifiersMap: Map<string, FieldSchema>): string => {
  return structuredClone(rule.modifiers).map((m: SegmentRule, idx: number, arr: Array<SegmentRule>) => {
    const schema = modifiersMap.get(m.fieldUid);
    const name = schema?.name ?? '';
    
    // Special handling for Exclude MPP Opens - it doesn't need operator/value display
    if (name === 'Exclude MPP Opens') {
      return `${name}${(arr.length > 1 && idx + 1 !== arr.length) ? ' and ' : ''}`;
    }

    const operator = getOperatorDisplay(m, schema);
    const value = getValueDisplay(m, schema);

    return `${name} ${operator} ${value}${(arr.length > 1 && idx + 1 !== arr.length) ? ' and ' : ''}`
  }).join('');
}

export const hasBooleanRule = (rule: SegmentRule): boolean => {
  return rule.operator === 'True' || rule.operator === 'False';
};

export const formattedBoolValue = (rule: SegmentRule, flatFieldsMap: Map<string, FieldSchema>): string => {
  const selectedFieldName = getSelectedFieldName(rule, flatFieldsMap);
  const booleanFieldNames = aliases.flatMap(({ positive, negative }) => [positive, negative].filter(Boolean));
  
  if (booleanFieldNames.includes(selectedFieldName)) { // prevent boolean value when it is implied
    return '';
  }

  return 'Has ';
};

export const getSelectedFieldName = (rule: SegmentRule, flatFieldsMap: Map<string, FieldSchema>, selectedBooleanValue?: boolean, preview?: boolean): string => {
  if (!flatFieldsMap.has(rule.fieldUid)) {
    return '';
  }

  let fieldName = flatFieldsMap.get(rule.fieldUid).name;
  const alias = aliases.find(alias => alias.initialFieldName === fieldName);

  if (alias && !preview) {
    if (selectedBooleanValue && alias.positive) {
      fieldName = alias.positive;
    } else if (!selectedBooleanValue && alias.negative) {
      fieldName = alias.negative;
    }
  }

  if (alias && preview) {
    if (rule.operator === 'True') {
      fieldName = alias.positive;
    } else if (rule.operator === 'False') {
      fieldName = alias.negative;
    }
  }

  return fieldName || '';
}

export const hasConfiguredModifiers = (rule: SegmentRule): boolean => {
  return Array.isArray(rule.modifiers) && rule.modifiers.length > 0;
}
